/* eslint-disable @typescript-eslint/no-floating-promises */
import { AssessmentState, ComponentBusinessId, padFormatNumber } from '@cambridgeassessment/lib-ee-commons';
import { getSectionsByParams, updateSectionsAssessmentState } from '../../service/rmaFailures';
import { NotificationActionTypes } from '../notification/types';
import { RootState } from '..';
import { displayNotification } from '../notification/actions';
import * as actions from './actions';
import { RmaErrorManagementActionTypes } from './types';
import { SortingParams } from '../../pages/Content/MalpracticeReports/components/ReportTable/ReportTable';
import { RmaErrors, SearchValues } from '../../types/RmaErrors';

type Dispatchable = RmaErrorManagementActionTypes | NotificationActionTypes | ((action: Dispatchable) => Promise<void>);
type Dispatch = (action: Dispatchable) => void | Promise<void>;

export const getSectionsWithRmaErrors =
  (
    businessUnitId: ComponentBusinessId['businessUnitId'],
    searchValues: SearchValues,
    assessmentState?: AssessmentState[],
    sortingParams?: SortingParams<RmaErrors>,
  ) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const { rmaErrors, nextPageUrl } = getState().rmaSubmissionFailures;

      const { businessUnit } = getState().userData;
      if (!businessUnit) throw new Error('BusinessId is not assigned');

      if (rmaErrors && rmaErrors.length > 0 && !nextPageUrl) {
        return;
      }

      if (!rmaErrors) {
        dispatch(actions.setIsRmaErrorsLoading(true));
      }

      const { results, nextUrl, total } = await getSectionsByParams(
        businessUnitId,
        searchValues,
        nextPageUrl,
        assessmentState,
        sortingParams,
      );

      const mappedTableRows = results.map((section) => {
        return {
          assessmentComponent: `${section.assessment.code}/${section.component.number}`,
          centreId: section.centre.id,
          learnerNumber: padFormatNumber(section.learnerNumber || 0, 4),
          sectionName: section.name,
          filename:
            (section.state.fileAssociations &&
              section.state.fileAssociations?.length > 0 &&
              section.state.fileAssociations[0].originalFileName) ||
            '',
          rmaError: section.state.assessmentError || '',
          state: section.state.assessmentState,
          submissionDate: section.state.rmaStateChangeDate || '',
          submittedBy: section.state.rmaResubmittedBy?.name ?? 'Centre',
        };
      });

      dispatch(actions.setRmaTableData(mappedTableRows, total ? +total : 0, nextUrl));
      dispatch(actions.setIsRmaErrorsLoading(false));
    } catch {
      dispatch(actions.setIsRmaErrorsLoading(false));
      dispatch(displayNotification('Failed to get Rma Errors'));
    }
  };

export const updateSectionsWithRmaErrors =
  (businessUnitId: ComponentBusinessId['businessUnitId'], searchValue: SearchValues) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    try {
      const { businessUnit } = getState().userData;
      if (!businessUnit) throw new Error('BusinessId is not assigned');

      await updateSectionsAssessmentState(businessUnitId, searchValue);

      dispatch(actions.resetState());
      dispatch(displayNotification("Sections are being re-sent to RM - see 'In Progress' status"));

      await getSectionsWithRmaErrors(businessUnitId, searchValue, [
        AssessmentState.FAILED_TO_SEND_FOR_ASSESSMENT,
        AssessmentState.READY_FOR_RM_RERUN,
      ])(dispatch, getState);
    } catch {
      dispatch(displayNotification('Failed to update Sections data'));
    }
  };
